import type { Attachment } from "@/types/attachment";
import { ossUploader } from "@/util/multipartUpload";
import { assertNever } from "./assertNever";

/**
 * 上传附件并返回 URL 列表
 * - 对于文件类型的附件，上传到服务器并返回 URL
 * - 对于 URL 类型的附件，直接返回 URL
 *
 * @param attachments 附件列表
 * @returns URL 字符串数组
 * @throws 如果任何文件上传失败
 */
export async function uploadAttachments(
  attachments: Attachment[]
): Promise<string[]> {
  if (attachments.length === 0) {
    return [];
  }

  // Parallelize uploads for all attachments
  const uploadPromises = attachments.map(async (attachment) => {
    if (attachment.type === "file") {
      try {
        const url = await ossUploader.uploadFile(attachment.file);
        if (!url) {
          throw new Error(`Upload succeeded but no downloadUrl returned`);
        }
        return url;
      } catch (error) {
        const fileName = attachment.file.name;
        console.error(`Failed to upload file: ${fileName}`, error);
        throw new Error(
          `Failed to upload file: ${fileName}. ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    } else if (attachment.type === "url") {
      // URL 类型直接返回 URL
      return attachment.url;
    } else {
      assertNever(attachment);
    }
  });

  const urls = await Promise.all(uploadPromises);
  // 过滤掉空字符串
  return urls.filter((url): url is string => !!url);
}
